'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { createClient } from '@/lib/supabase-browser';
import { toast } from 'sonner';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultView?: 'login' | 'register';
}

// Common disposable email domains to block
const DISPOSABLE_DOMAINS = [
  'mailinator.com', 'temp-mail.org', 'guerrillamail.com', '10minutemail.com', 
  'throwawaymail.com', 'tempmail.com', 'getnada.com', 'yopmail.com',
  'sharklasers.com', 'dispostable.com', 'guerrillamailblock.com'
];

export function AuthModal({ isOpen, onClose, defaultView = 'login' }: AuthModalProps) {
  const router = useRouter();
  const [view, setView] = React.useState<'login' | 'register'>(defaultView);
  const [loading, setLoading] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [name, setName] = React.useState('');

  const supabase = createClient();

  const validateEmail = (emailStr: string) => {
    const domain = emailStr.split('@')[1]?.toLowerCase();
    if (DISPOSABLE_DOMAINS.includes(domain)) {
      return false;
    }
    return true;
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail(email)) {
      toast.error('Temporary email domains are not allowed', {
        description: 'Please use a professional or standard personal email address.'
      });
      return;
    }

    setLoading(true);

    try {
      if (view === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success('Signed in successfully!');
        onClose();
        router.refresh();
      } else {
        onClose();
        router.push('/register');
      }
    } catch (error: any) {
      toast.error(error.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        }
      });
      if (error) throw error;
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleLinkedinSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        }
      });
      if (error) throw error;
    } catch (error: any) {
      toast.error(error.message || 'LinkedIn login failed');
    }
  };

  const handleSignUpClick = () => {
    onClose();
    router.push('/register');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-[420px] max-h-[90vh] overflow-y-auto overflow-x-hidden bg-card/95 backdrop-blur-2xl border border-border/60 rounded-2xl sm:rounded-[2rem] shadow-2xl p-0">
        <div className="p-6 sm:p-8 space-y-5 sm:space-y-6">
          <DialogHeader className="space-y-3">
            <div className="size-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-2">
              <Lock className="size-6 text-primary" />
            </div>
            <DialogTitle className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              Welcome Back
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs sm:text-sm">
              Sign in to access your saved tools and dashboard.
            </DialogDescription>
            <div className="flex items-center gap-2 mt-2 py-1.5 px-3 bg-success/10 border border-success/20 rounded-full w-fit">
               <span className="size-1.5 bg-success rounded-full animate-pulse" />
               <span className="text-[9px] font-bold uppercase tracking-widest text-success">Secured via Supabase Auth</span>
            </div>
          </DialogHeader>

          <form onSubmit={handleAuth} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="name@company.com" 
                  className="pl-10 h-12 bg-muted/40 border-border/60 rounded-xl focus:ring-primary"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input 
                  id="password" 
                  type={showPassword ? 'text' : 'password'} 
                  placeholder="••••••••" 
                  className="pl-10 pr-10 h-12 bg-muted/40 border-border/60 rounded-xl focus:ring-primary"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl mt-4 shadow-md shadow-primary/20"
              disabled={loading}
            >
              {loading ? <Loader2 className="size-5 animate-spin" /> : 'Sign In'}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border/40" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Button 
              variant="outline" 
              className="h-12 border-border/60 hover:bg-muted rounded-xl text-foreground gap-2 font-medium"
              onClick={handleGoogleSignIn}
            >
              <svg className="size-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.25.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google
            </Button>

            <Button 
              variant="outline" 
              className="h-12 border-border/60 hover:bg-muted rounded-xl text-foreground gap-2 font-medium"
              onClick={handleLinkedinSignIn}
            >
              <svg className="size-5 text-[#0077b5]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
              LinkedIn
            </Button>
          </div>
        </div>

        <div className="bg-muted/30 p-6 text-center border-t border-border/40 space-y-3">
          <p className="text-sm text-muted-foreground">
            Don&apos;t have an account?{' '}
            <button 
              type="button"
              onClick={handleSignUpClick}
              className="text-primary font-bold hover:underline"
            >
              Sign Up
            </button>
          </p>
          <div className="pt-2 border-t border-border/20">
             <p className="text-[10px] text-muted-foreground/60 leading-relaxed italic">
               Registration is purely for tool ownership verification. We value your privacy and do not send unsolicited marketing emails.
             </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
